import type { Database } from 'sql.js';
import { queryAll, queryOne, type SqlJsBind } from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import { foldParticipanteBusquedaText } from '../domain/participanteBusquedaText.ts';
import type { ParticipanteBusquedaLookupResult } from '../domain/participanteBusquedaLookupTypes.ts';

const PARTICIPANT_LOOKUP_SQL = `SELECT p.*, c.nombre AS comuna_nombre
  FROM participantes p
  LEFT JOIN comunas c ON c.id = p.comuna_id`;

/** Max rows returned for participant search pickers (document or name). */
export const PARTICIPANTE_BUSQUEDA_CANDIDATES_LIMIT = 25;

/** Escapes `%`, `_`, and `\` for SQLite `LIKE` with `ESCAPE '\\'`. */
function escapeSqlLikeFragment(fragment: string): string {
  return fragment.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

function participantesHasNombreNormalizado(db: Database): boolean {
  const row = queryOne(
    db,
    `SELECT 1 AS ok FROM pragma_table_info('participantes') WHERE name = 'nombre_normalizado' LIMIT 1`,
    [],
  );
  return row != null;
}

function resolvePartialMatches(
  rows: readonly Record<string, SqlJsBind>[],
): ParticipanteBusquedaLookupResult {
  if (rows.length === 1) return { status: 'found', row: rows[0]! };
  if (rows.length > 1) return { status: 'ambiguous' };
  return { status: 'not_found' };
}

function documentoPrefixLikeTerm(term: string): string {
  return `${escapeSqlLikeFragment(term)}%`;
}

function queryParticipantesByDocumentoPrefix(
  db: Database,
  term: string,
  limit: number,
): Record<string, SqlJsBind>[] {
  return queryAll(
    db,
    `${PARTICIPANT_LOOKUP_SQL} WHERE p.documento LIKE ? ESCAPE '\\' AND p.estado = 1 ORDER BY p.documento LIMIT ?`,
    [documentoPrefixLikeTerm(term), limit],
  );
}

/**
 * Resolves an active participant row by exact document, document prefix, exact name
 * (case-insensitive), or a unique partial name match (accent-insensitive when
 * `nombre_normalizado` is present).
 */
export function lookupParticipanteSqlRowByDocumentoOrNombre(
  db: Database,
  term: string,
): ParticipanteBusquedaLookupResult {
  const trimmed = term.trim();
  if (!trimmed) return { status: 'empty' };

  const byDocumento = queryOne(
    db,
    `${PARTICIPANT_LOOKUP_SQL} WHERE p.documento = ? AND p.estado = 1`,
    [trimmed],
  );
  if (byDocumento) return { status: 'found', row: byDocumento };

  const byDocumentoParcial = queryParticipantesByDocumentoPrefix(db, trimmed, 2);
  const documentoParcial = resolvePartialMatches(byDocumentoParcial);
  if (documentoParcial.status !== 'not_found') return documentoParcial;

  const byNombreExacto = queryOne(
    db,
    `${PARTICIPANT_LOOKUP_SQL} WHERE TRIM(p.nombre) = ? COLLATE NOCASE AND p.estado = 1`,
    [trimmed],
  );
  if (byNombreExacto) return { status: 'found', row: byNombreExacto };

  const likeTerm = `%${escapeSqlLikeFragment(trimmed)}%`;
  const byNombreParcial = queryAll(
    db,
    `${PARTICIPANT_LOOKUP_SQL} WHERE p.nombre COLLATE NOCASE LIKE ? ESCAPE '\\' AND p.estado = 1 LIMIT 2`,
    [likeTerm],
  );
  if (byNombreParcial.length === 1) return { status: 'found', row: byNombreParcial[0]! };
  if (byNombreParcial.length > 1) return { status: 'ambiguous' };

  if (participantesHasNombreNormalizado(db)) {
    const folded = foldParticipanteBusquedaText(trimmed);
    if (folded) {
      const byNormExact = queryOne(
        db,
        `${PARTICIPANT_LOOKUP_SQL} WHERE p.nombre_normalizado = ? AND p.estado = 1`,
        [folded],
      );
      if (byNormExact) return { status: 'found', row: byNormExact };

      const foldedLike = `%${escapeSqlLikeFragment(folded)}%`;
      const byNormPartial = queryAll(
        db,
        `${PARTICIPANT_LOOKUP_SQL} WHERE p.nombre_normalizado LIKE ? ESCAPE '\\' AND p.estado = 1 LIMIT 2`,
        [foldedLike],
      );
      const partial = resolvePartialMatches(byNormPartial);
      if (partial.status !== 'not_found') return partial;
    }
  }

  return { status: 'not_found' };
}

/**
 * Lists active participants matching document, document prefix, exact name, or partial name
 * (same rules as {@link lookupParticipanteSqlRowByDocumentoOrNombre}, up to `limit` rows).
 */
export function listParticipanteSqlRowsByDocumentoOrNombre(
  db: Database,
  term: string,
  limit = PARTICIPANTE_BUSQUEDA_CANDIDATES_LIMIT,
): Record<string, SqlJsBind>[] {
  const trimmed = term.trim();
  if (!trimmed || limit <= 0) return [];

  const byDocumento = queryOne(
    db,
    `${PARTICIPANT_LOOKUP_SQL} WHERE p.documento = ? AND p.estado = 1`,
    [trimmed],
  );
  if (byDocumento) return [byDocumento];

  const byDocumentoParcial = queryParticipantesByDocumentoPrefix(db, trimmed, limit);
  if (byDocumentoParcial.length > 0) return byDocumentoParcial;

  const byNombreExacto = queryOne(
    db,
    `${PARTICIPANT_LOOKUP_SQL} WHERE TRIM(p.nombre) = ? COLLATE NOCASE AND p.estado = 1`,
    [trimmed],
  );
  if (byNombreExacto) return [byNombreExacto];

  const likeTerm = `%${escapeSqlLikeFragment(trimmed)}%`;
  const byNombreParcial = queryAll(
    db,
    `${PARTICIPANT_LOOKUP_SQL} WHERE p.nombre COLLATE NOCASE LIKE ? ESCAPE '\\' AND p.estado = 1 ORDER BY p.nombre LIMIT ?`,
    [likeTerm, limit],
  );
  if (byNombreParcial.length > 0) return byNombreParcial;

  if (participantesHasNombreNormalizado(db)) {
    const folded = foldParticipanteBusquedaText(trimmed);
    if (folded) {
      const byNormExact = queryOne(
        db,
        `${PARTICIPANT_LOOKUP_SQL} WHERE p.nombre_normalizado = ? AND p.estado = 1`,
        [folded],
      );
      if (byNormExact) return [byNormExact];

      const foldedLike = `%${escapeSqlLikeFragment(folded)}%`;
      const byNormPartial = queryAll(
        db,
        `${PARTICIPANT_LOOKUP_SQL} WHERE p.nombre_normalizado LIKE ? ESCAPE '\\' AND p.estado = 1 ORDER BY p.nombre LIMIT ?`,
        [foldedLike, limit],
      );
      if (byNormPartial.length > 0) return byNormPartial;
    }
  }

  return [];
}

/** Back-compat helper: returns the row only when the lookup is unambiguous. */
export function findParticipanteSqlRowByDocumentoOrNombre(
  db: Database,
  term: string,
): Record<string, SqlJsBind> | null {
  const lookup = lookupParticipanteSqlRowByDocumentoOrNombre(db, term);
  return lookup.status === 'found' ? lookup.row : null;
}
